<script setup lang="ts">
import Notifications from '@/core/components/Notifications.vue';
import { Language, NxtMainLayout, NxtNotificationBanner, NxtPosthog, NxtSidebar, NxtSidebarNavigationItem } from '@nxtpeople/nxt-styles';
import { ModuleKey } from '@nxtpeople/nxt-vue-library';
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import PageHeader from './core/components/PageHeader.vue';
import { useApp } from './core/composables/useApp';
import { checkAccess } from './core/helpers/auth.helper';
import { http } from './core/helpers/axiosHelper';

useApp();

const { getters, state } = useStore();
const router = useRouter();
const { locale, t } = useI18n();

const languages = computed(() => getters.languages);
const userInfo = computed(() => state.userInfo.userInfo);

function changeLanguage() {
   if (languages.value.length == 0 || !userInfo.value) return;
   locale.value = languages.value.find((l: Language) => l.id == userInfo.value.language_id)?.key?.toLowerCase() || 'nl';
}

watch([languages, userInfo], changeLanguage);

const notification = computed(() => {
   return state.maintenanceStore?.system_notification;
});

// #region posthog
const is_production = computed(() => import.meta.env.VITE_APP_ENV === 'prod');
const API_KEY = import.meta.env.VITE_APP_POSTHOG_API_KEY;
const API_HOST = import.meta.env.VITE_APP_POSTHOG_API_HOST;
// #endregion

//#region Navigation
const navigationItems = computed<Array<NxtSidebarNavigationItem>>(() => [
   {
      key: 'journeys',
      to: '/journey',
      icon: 'fas fa-rectangle-list',
      label: t('journeys'),
   },
   {
      key: 'dashboard',
      to: '/dashboard',
      icon: 'fas fa-gauge-high',
      label: t('dashboard'),
      hide: !checkAccess('view', userInfo.value?.scopes?.dashboards),
   },
   {
      key: 'admin',
      to: '/admin',
      icon: 'fas fa-user-gear',
      label: t('admin'),
      hide: !checkAccess('admin', userInfo.value?.scopes?.tenants),
   },
   {
      key: 'notion',
      to: 'https://nxtpeople.notion.site/NxtForm-Templates-5bfad19f87e04d259d700a6ef0ae1a9e',
      icon: 'fas fa-books',
      label: t('notion'),
      external: true,
   },
]);
//#endregion
</script>

<template>
   <NxtMainLayout
      :http
      :user-info="userInfo"
   >
      <template #status-banner>
         <NxtNotificationBanner v-if="notification?.id">
            <span v-purify-html="notification.message[$i18n.locale] || notification.message.en"></span>
         </NxtNotificationBanner>
      </template>
      <template #header>
         <PageHeader />
      </template>

      <template #nav>
         <NxtSidebar
            :loading="!userInfo"
            :module-key="ModuleKey.NxtForm"
            :navigation-items="navigationItems"
            :shared-routes="[]"
         />
      </template>
      <RouterView v-slot="{ Component }">
         <component
            v-if="Component"
            :is="Component"
         />
      </RouterView>

      <Notifications />

      <!-- POSTHOG -->
      <NxtPosthog
         v-if="is_production"
         :api-key="API_KEY"
         :api-host="API_HOST"
         :router="router"
         :user-info="userInfo"
      />
   </NxtMainLayout>
</template>

<style lang="scss" scoped>
.loading-spinner {
   display: flex;
   align-items: center;
   justify-content: center;
   width: 100%;
}

.content {
   display: flex;
   height: 100%;
   overflow: auto;
   flex-grow: 1;
}
</style>

<style lang="scss">
#app {
   display: flex;
   flex-direction: column;
   overflow: auto;
   height: 100%;
}

html {
   font-family: Poppins, Lato, Avenir, Helvetica, Arial, sans-serif;
   -webkit-font-smoothing: antialiased;
   -moz-osx-font-smoothing: grayscale;
}
</style>
